import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

// "Take it with you": draws a lock-screen wallpaper of a dozen phrases on a
// canvas and downloads it as a PNG. Everything happens in the browser -
// nothing is uploaded. The phrases arrive already picked by the server
// (TravelPhrases::TakeawayPhrases).
//
// The layout and wrapping below are pure functions of a `measure` callback,
// so they're testable without a canvas (jsdom has none); only drawWallpaper
// touches the 2D context.

export const WIDTH = 1170;
export const HEIGHT = 2532;

// Lock screens put the clock and date over roughly the top 30%, and the
// flashlight / camera buttons over the bottom corners.
const PHRASES_TOP = 980;
const PHRASES_BOTTOM = HEIGHT - 330;
const MARGIN = 90;
const GUTTER = 70;
const ACCENT_WIDTH = 6;
const ACCENT_GAP = 22;
const COLUMN_WIDTH = (WIDTH - 2 * MARGIN - GUTTER) / 2;
const TEXT_WIDTH = COLUMN_WIDTH - ACCENT_WIDTH - ACCENT_GAP;
const PHRASE_GAP = 52;
const MIN_SCALE = 0.6;

// System faces only, with fallbacks that cover CJK, Thai, Devanagari, Arabic
// and the rest on the phones and desktops people will make this on.
const SANS = "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, \"Noto Sans\", " +
  "\"Hiragino Sans\", \"PingFang SC\", \"Microsoft YaHei\", \"Noto Sans CJK JP\", \"Noto Sans Thai\", \"Leelawadee UI\", sans-serif";
const SERIF = "Georgia, \"Iowan Old Style\", \"Palatino Linotype\", Palatino, \"Noto Serif\", serif";

// Near-white on near-black: contrast comes from these, never from the flag.
const INK = {
  background: ["#171b23", "#0c0e13"],
  en: "#9aa3b1",
  footer: "#7f8897",
  heading: "#f4f5f7",
  kicker: "#9aa3b1",
  pron: "#ccd3dd",
  target: "#ffffff",
};

const STYLES = {
  en: { color: INK.en, font: (px) => `${px}px ${SANS}`, lineHeight: 1.25, size: 28, spaceAfter: 0 },
  pron: { color: INK.pron, font: (px) => `italic ${px}px ${SANS}`, lineHeight: 1.25, size: 32, spaceAfter: 8 },
  // Tall line height: Thai and Vietnamese stack marks above the letters.
  target: { color: INK.target, font: (px) => `bold ${px}px ${SANS}`, lineHeight: 1.3, size: 54, spaceAfter: 6 },
};

const RTL = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;

function segmenter(granularity) {
  if (typeof Intl === "undefined" || !Intl.Segmenter) return null;

  return new Intl.Segmenter(undefined, { granularity });
}

// Whole characters, so a Thai vowel or a combining accent never ends up on a
// different line from its letter.
function graphemes(text) {
  const seg = segmenter("grapheme");
  return seg ? Array.from(seg.segment(text), (s) => s.segment) : Array.from(text);
}

// Breakable pieces: words and the spaces between them, or - for scripts
// written without spaces (Japanese, Chinese, Thai) - the browser's own word
// segmentation, falling back to single characters.
export function breakpoints(text) {
  if (/\s/.test(text.trim())) return text.split(/(\s+)/).filter(Boolean);

  const seg = segmenter("word");
  return seg ? Array.from(seg.segment(text), (s) => s.segment) : graphemes(text);
}

// Greedy line filling. `measure(text)` returns the drawn width of text; a
// piece wider than a whole line is broken between characters.
export function wrapText(text, maxWidth, measure) {
  const lines = [];
  let line = "";
  const flush = () => {
    if (line.trim()) lines.push(line.trim());
    line = "";
  };

  breakpoints(String(text || "").trim()).forEach((piece) => {
    if (/^\s+$/.test(piece)) {
      if (line) line += piece;
      return;
    }
    if (measure((line + piece).trim()) <= maxWidth) {
      line += piece;
      return;
    }
    flush();
    if (measure(piece) <= maxWidth) {
      line = piece;
      return;
    }
    graphemes(piece).forEach((ch) => {
      if (line && measure(line + ch) > maxWidth) flush();
      line += ch;
    });
  });
  flush();

  return lines;
}

// Flag colours are accents only, and some (navy, black) vanish on a dark
// ground: mix them towards white until they're visible.
export function liftColor(hex, minLuminance = 0.3) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!match) return "#8a94a6";

  let rgb = [0, 2, 4].map((i) => parseInt(match[1].slice(i, i + 2), 16));
  const luminance = ([r, g, b]) => {
    const lin = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };
  for (let i = 0; i < 10 && luminance(rgb) < minLuminance; i++) {
    rgb = rgb.map((c) => Math.round(c + (255 - c) * 0.2));
  }

  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

function reading(phrase) {
  return phrase.pron || phrase.romanization || "";
}

// Lines and height of each phrase at one scale. `measure(font, text)`.
function blocks(phrases, scale, measure) {
  return phrases.map((phrase) => {
    let height = 0;
    const parts = ["target", "pron", "en"].map((key) => {
      const style = STYLES[key];
      const size = Math.round(style.size * scale);
      const font = style.font(size);
      const text = key === "pron" ? reading(phrase) : phrase[key];
      const lines = text ? wrapText(text, TEXT_WIDTH, (t) => measure(font, t)) : [];
      const lineHeight = Math.round(size * style.lineHeight);
      height += lines.length * lineHeight + (lines.length ? Math.round(style.spaceAfter * scale) : 0);

      return { color: style.color, font, key, lineHeight, lines, rtl: key === "target" && RTL.test(text || "") };
    });

    return { height, parts, phrase };
  });
}

function columnHeight(column, gap) {
  return column.reduce((sum, block) => sum + block.height, 0) + Math.max(column.length - 1, 0) * gap;
}

// Two columns, in reading order down the left then the right, split where
// the taller one is shortest; shrinks the type until both fit, then drops
// phrases from the end if they still don't.
export function planWallpaper(phrases, measure, available = PHRASES_BOTTOM - PHRASES_TOP) {
  let list = phrases.filter((phrase) => phrase && phrase.target);

  while (list.length) {
    for (let scale = 1; scale >= MIN_SCALE - 0.001; scale -= 0.05) {
      const gap = Math.round(PHRASE_GAP * scale);
      const laid = blocks(list, scale, measure);
      let best = null;
      for (let split = Math.ceil(laid.length / 2); split <= laid.length; split++) {
        const columns = [laid.slice(0, split), laid.slice(split)];
        const tallest = Math.max(...columns.map((column) => columnHeight(column, gap)));
        if (!best || tallest < best.tallest) best = { columns, tallest };
      }
      if (best.tallest <= available) return { columns: best.columns, gap, scale };
    }
    list = list.slice(0, -1);
  }

  return { columns: [[], []], gap: PHRASE_GAP, scale: 1 };
}

export function footerText(languageName, slug) {
  return `Wikivoyage ${languageName} phrasebook · CC BY-SA 4.0 · clozemaster.com/travel-phrases/${slug}`;
}

export function drawWallpaper(canvas, { colors, languageName, phrases, slug }) {
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  const accents = (colors && colors.length ? colors : ["#8a94a6"]).map((color) => liftColor(color));
  const measure = (font, text) => {
    ctx.font = font;
    return ctx.measureText(text).width;
  };

  const ground = ctx.createLinearGradient(0, 0, 0, HEIGHT);
  ground.addColorStop(0, INK.background[0]);
  ground.addColorStop(1, INK.background[1]);
  ctx.fillStyle = ground;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // A faint wash of the first colour behind the clock, and the flag band.
  const glow = ctx.createRadialGradient(WIDTH * 0.8, HEIGHT * 0.12, 0, WIDTH * 0.8, HEIGHT * 0.12, WIDTH);
  glow.addColorStop(0, `${accents[0]}26`);
  glow.addColorStop(1, `${accents[0]}00`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const stripe = WIDTH / (colors && colors.length ? colors.length : 1);
  (colors && colors.length ? colors : accents).forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(Math.round(i * stripe), 0, Math.ceil(stripe), 18);
  });

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.direction = "ltr";

  ctx.fillStyle = INK.kicker;
  ctx.font = `600 28px ${SANS}`;
  if ("letterSpacing" in ctx) ctx.letterSpacing = "4px";
  ctx.fillText("TRAVEL PHRASES", MARGIN, 770);
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

  const heading = `${languageName} essentials`;
  let headingSize = 78;
  while (headingSize > 44 && measure(`bold ${headingSize}px ${SERIF}`, heading) > WIDTH - 2 * MARGIN) headingSize -= 2;
  ctx.fillStyle = INK.heading;
  ctx.font = `bold ${headingSize}px ${SERIF}`;
  ctx.fillText(heading, MARGIN, 816);

  accents.forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(MARGIN + i * 54, 816 + headingSize + 28, 42, 6);
  });

  const plan = planWallpaper(phrases, measure);
  let colorIndex = 0;
  plan.columns.forEach((column, c) => {
    const x = MARGIN + c * (COLUMN_WIDTH + GUTTER);
    let y = PHRASES_TOP;
    column.forEach((block) => {
      ctx.fillStyle = accents[colorIndex++ % accents.length];
      ctx.fillRect(x, y + 6, ACCENT_WIDTH, Math.max(block.height - 12, 12));

      let lineY = y;
      block.parts.forEach((part) => {
        ctx.font = part.font;
        ctx.fillStyle = part.color;
        ctx.direction = part.rtl ? "rtl" : "ltr";
        ctx.textAlign = "left";
        part.lines.forEach((line) => {
          ctx.fillText(line, x + ACCENT_WIDTH + ACCENT_GAP, lineY);
          lineY += part.lineHeight;
        });
        if (part.lines.length) lineY += Math.round(STYLES[part.key].spaceAfter * plan.scale);
      });
      ctx.direction = "ltr";
      y += block.height + plan.gap;
    });
  });

  const footerFont = `24px ${SANS}`;
  const footerLines = wrapText(footerText(languageName, slug), WIDTH - 2 * MARGIN, (t) => measure(footerFont, t));
  ctx.font = footerFont;
  ctx.fillStyle = INK.footer;
  ctx.textAlign = "center";
  footerLines.forEach((line, i) => ctx.fillText(line, WIDTH / 2, HEIGHT - 250 + i * 34));
}

function download(canvas, filename) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Couldn't create the image"));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Safari needs the URL a moment after the click.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      resolve();
    }, "image/png");
  });
}

export default function Wallpaper({ colors, languageName, phrases, slug }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const canvas = document.createElement("canvas");
      drawWallpaper(canvas, { colors, languageName, phrases, slug });
      await download(canvas, `${slug}-travel-phrases-wallpaper.png`);
    } catch (e) {
      setError("Sorry, your browser couldn't make the image.");
    } finally {
      setBusy(false);
    }
  }, [colors, languageName, phrases, slug]);

  return (
    <span className="tp-wallpaper">
      <button
        className="tp-takeaway-link"
        disabled={busy}
        onClick={save}
        title="A lock-screen image of the essentials, made in your browser"
        type="button"
      >
        {busy ? "Drawing…" : "📱 Phone wallpaper"}
      </button>
      {error && <span className="tp-takeaway-error" role="alert">{error}</span>}
    </span>
  );
}

Wallpaper.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string),
  languageName: PropTypes.string.isRequired,
  phrases: PropTypes.arrayOf(PropTypes.shape({
    en: PropTypes.string,
    pron: PropTypes.string,
    romanization: PropTypes.string,
    target: PropTypes.string.isRequired,
  })).isRequired,
  slug: PropTypes.string.isRequired,
};
